import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ChevronDown, 
  ChevronRight, 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Calendar,
  RefreshCw,
  Package,
  Building2,
  AlertCircle,
  Store,
  Settings
} from 'lucide-react';
import { BACKEND_URL as __BACKEND_URL__ } from "../../lib/backend-url";

const API = __BACKEND_URL__;

// Store display names
const STORE_NAMES = {
  'all': 'Todas as Lojas',
  'vencidinho_cangaiba': 'Vencidinho Cangaíba',
  'bestby_sjc': 'Best By SJC',
  'bestby_jacarei': 'Best By Jacareí',
  'bestby_panamby': 'Best By Panamby'
};

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

// DRE Category Row Component
const DRECategoryRow = ({ category, months, expanded, onToggle }) => {
  const isPositive = category.type === 'revenue' || category.type === 'financial';
  
  return (
    <>
      <tr 
        className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
          category.type === 'revenue' ? 'bg-emerald-50/50' : 
          category.type === 'cost' ? 'bg-red-50/50' : 
          category.type === 'expense' ? 'bg-amber-50/30' : ''
        }`}
        onClick={() => onToggle(category.id)}
      >
        <td className="py-3 px-4 sticky left-0 bg-inherit">
          <div className="flex items-center gap-2">
            {category.subcategories?.length > 0 ? (
              expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />
            ) : <div className="w-4" />}
            <span className="font-semibold text-slate-800">{category.name}</span>
          </div>
        </td>
        {months.map(m => (
          <td key={m.number} className="py-3 px-3 text-right">
            <span className={`font-medium ${isPositive ? 'text-emerald-600' : 'text-slate-700'}`}>
              {formatCurrency(category.totals[m.number])}
            </span>
          </td>
        ))}
        <td className="py-3 px-4 text-right bg-slate-50 sticky right-0">
          <span className={`font-bold ${isPositive ? 'text-emerald-600' : 'text-slate-800'}`}>
            {formatCurrency(category.year_total)}
          </span>
        </td>
      </tr>
      
      {/* Subcategories */}
      {expanded && category.subcategories?.map(subcat => (
        <tr key={subcat.id} className="border-b border-slate-100 bg-white">
          <td className="py-2 px-4 pl-10 sticky left-0 bg-white">
            <span className="text-sm text-slate-600">{subcat.name}</span>
          </td>
          {months.map(m => (
            <td key={m.number} className="py-2 px-3 text-right">
              <span className="text-sm text-slate-500">
                {formatCurrency(subcat.values[m.number])}
              </span>
            </td>
          ))}
          <td className="py-2 px-4 text-right bg-slate-50/50 sticky right-0">
            <span className="text-sm font-medium text-slate-600">
              {formatCurrency(subcat.year_total)}
            </span>
          </td>
        </tr>
      ))}
    </>
  );
};

// Main P&L Page Component
export const PnLPage = () => {
  const [activeTab, setActiveTab] = useState('dre');
  const [selectedYear, setSelectedYear] = useState(2026); // Default to current year
  const [selectedStore, setSelectedStore] = useState('vencidinho_cangaiba'); // Default store
  const [availableYears, setAvailableYears] = useState([]);
  const [availableStores, setAvailableStores] = useState([]);
  const [dreData, setDreData] = useState(null);
  const [projecaoData, setProjecaoData] = useState(null);
  const [fluxoCaixaData, setFluxoCaixaData] = useState(null);
  const [consolidatedData, setConsolidatedData] = useState(null); // For "all stores" view
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // State for expanded day details in fluxo de caixa
  const [expandedDay, setExpandedDay] = useState(null); // { date: '2026-01-24', type: 'cmv' | 'outras' }
  const [fluxoCaixaDays, setFluxoCaixaDays] = useState(30); // Number of days to show
  const [showCustomDays, setShowCustomDays] = useState(false); // Show custom days input
  const [customDaysInput, setCustomDaysInput] = useState(''); // Custom days input value

  // Load available stores
  useEffect(() => {
    const loadStores = async () => {
      try {
        const response = await axios.get(`${API}/api/pnl/stores`);
        const stores = response.data.stores || [];
        setAvailableStores(stores);
      } catch (error) {
        console.error('Error loading stores:', error);
      }
    };
    loadStores();
  }, []);

  // Load available years (only depends on store change)
  useEffect(() => {
    const loadYears = async () => {
      try {
        const storeParam = selectedStore && selectedStore !== 'all' ? `?store_id=${selectedStore}` : '';
        const response = await axios.get(`${API}/api/pnl/anos-disponiveis${storeParam}`);
        const years = response.data.years || [];
        setAvailableYears(years);
      } catch (error) {
        console.error('Error loading years:', error);
      }
    };
    loadYears();
  }, [selectedStore]);

  // Load DRE data
  const loadDRE = async (year, storeId) => {
    if (!year) return;
    setLoading(true);
    try {
      if (storeId === 'all') {
        // Load data for all stores and consolidate
        const storeIds = availableStores.map(s => s.id);
        const responses = await Promise.all(
          storeIds.map(sid => 
            axios.get(`${API}/api/pnl/dre?year=${year}&store_id=${sid}`)
              .catch(err => ({ data: null, error: err, store_id: sid }))
          )
        );
        
        // Consolidate DRE data from all stores
        const validResponses = responses.filter(r => r.data && !r.error);
        if (validResponses.length > 0) {
          const consolidated = consolidateDREData(validResponses.map(r => r.data));
          setDreData(consolidated);
        } else {
          setDreData(null);
        }
      } else {
        const storeParam = storeId ? `&store_id=${storeId}` : '';
        const response = await axios.get(`${API}/api/pnl/dre?year=${year}${storeParam}`);
        setDreData(response.data);
      }
    } catch (error) {
      console.error('Error loading DRE:', error);
    } finally {
      setLoading(false);
    }
  };

  // Consolidate DRE data from multiple stores
  const consolidateDREData = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return null;
    
    const base = JSON.parse(JSON.stringify(dataArray[0])); // Deep clone first response
    
    // Sum up values from all stores
    for (let i = 1; i < dataArray.length; i++) {
      const data = dataArray[i];
      if (!data || !data.categories) continue;
      
      // Sum categories
      data.categories.forEach(cat => {
        const baseCat = base.categories.find(c => c.id === cat.id);
        if (baseCat) {
          // Sum totals per month
          Object.keys(cat.totals || {}).forEach(month => {
            baseCat.totals[month] = (baseCat.totals[month] || 0) + (cat.totals[month] || 0);
          });
          baseCat.year_total = (baseCat.year_total || 0) + (cat.year_total || 0);
          
          // Sum subcategories
          cat.subcategories?.forEach(subcat => {
            const baseSubcat = baseCat.subcategories?.find(s => s.id === subcat.id);
            if (baseSubcat) {
              Object.keys(subcat.values || {}).forEach(month => {
                baseSubcat.values[month] = (baseSubcat.values[month] || 0) + (subcat.values[month] || 0);
              });
              baseSubcat.year_total = (baseSubcat.year_total || 0) + (subcat.year_total || 0);
            } else {
              baseCat.subcategories = baseCat.subcategories || [];
              baseCat.subcategories.push({ ...subcat });
            }
          });
        } else {
          base.categories.push({ ...cat });
        }
      });
      
      // Sum summary
      if (data.summary && base.summary) {
        Object.keys(data.summary).forEach(key => {
          base.summary[key] = (base.summary[key] || 0) + (data.summary[key] || 0);
        });
      }
    }
    
    return base;
  };

  // Load Projeção data
  const loadProjecao = async (storeId) => {
    setLoading(true);
    try {
      if (storeId === 'all') {
        // Load for all stores and consolidate
        const storeIds = availableStores.map(s => s.id);
        const responses = await Promise.all(
          storeIds.map(sid => 
            axios.get(`${API}/api/pnl/contas-pagar/projecao?days=30&store_id=${sid}`)
              .catch(err => ({ data: null, error: err }))
          )
        );
        
        const validResponses = responses.filter(r => r.data && !r.error);
        if (validResponses.length > 0) {
          const consolidated = consolidateProjecaoData(validResponses.map(r => r.data));
          setProjecaoData(consolidated);
        }
      } else {
        const storeParam = storeId ? `&store_id=${storeId}` : '';
        const response = await axios.get(`${API}/api/pnl/contas-pagar/projecao?days=30${storeParam}`);
        setProjecaoData(response.data);
      }
    } catch (error) {
      console.error('Error loading projecao:', error);
    } finally {
      setLoading(false);
    }
  };

  // Consolidate Projecao data from multiple stores
  const consolidateProjecaoData = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return null;
    
    const base = JSON.parse(JSON.stringify(dataArray[0]));
    
    for (let i = 1; i < dataArray.length; i++) {
      const data = dataArray[i];
      if (!data) continue;
      
      // Sum resumo
      if (data.resumo) {
        base.resumo.cmv.total += data.resumo.cmv?.total || 0;
        base.resumo.cmv.count += data.resumo.cmv?.count || 0;
        base.resumo.outras_despesas.total += data.resumo.outras_despesas?.total || 0;
        base.resumo.outras_despesas.count += data.resumo.outras_despesas?.count || 0;
        base.resumo.total_geral += data.resumo.total_geral || 0;
      }
      
      // Sum por_semana
      data.por_semana?.forEach((week, idx) => {
        if (base.por_semana[idx]) {
          base.por_semana[idx].cmv += week.cmv || 0;
          base.por_semana[idx].outras += week.outras || 0;
          base.por_semana[idx].total += week.total || 0;
        }
      });
    }
    
    return base;
  };

  // Load Fluxo de Caixa data (diário)
  const loadFluxoCaixa = async (storeId, days = fluxoCaixaDays) => {
    try {
      if (storeId === 'all') {
        // Load for all stores and consolidate
        const storeIds = availableStores.map(s => s.id);
        const responses = await Promise.all(
          storeIds.map(sid => 
            axios.get(`${API}/api/pnl/fluxo-caixa/diario?days=${days}&store_id=${sid}`)
              .catch(err => ({ data: null, error: err }))
          )
        );
        
        const validResponses = responses.filter(r => r.data && !r.error);
        if (validResponses.length > 0) {
          const consolidated = consolidateFluxoCaixaData(validResponses.map(r => r.data));
          setFluxoCaixaData(consolidated);
        }
      } else {
        const storeParam = storeId ? `&store_id=${storeId}` : '';
        const response = await axios.get(`${API}/api/pnl/fluxo-caixa/diario?days=${days}${storeParam}`);
        setFluxoCaixaData(response.data);
      }
    } catch (error) {
      console.error('Error loading fluxo caixa:', error);
    }
  };

  // Consolidate FluxoCaixa data from multiple stores
  const consolidateFluxoCaixaData = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return null;
    
    const base = JSON.parse(JSON.stringify(dataArray[0]));
    
    for (let i = 1; i < dataArray.length; i++) {
      const data = dataArray[i];
      if (!data) continue;
      
      // Sum totais
      if (data.totais) {
        base.totais.receita_projetada += data.totais.receita_projetada || 0;
        base.totais.despesas_cmv += data.totais.despesas_cmv || 0;
        base.totais.despesas_outras += data.totais.despesas_outras || 0;
        base.totais.despesas_total += data.totais.despesas_total || 0;
        base.totais.saldo += data.totais.saldo || 0;
      }
      
      // Sum por_dia
      data.por_dia?.forEach((day, idx) => {
        if (base.por_dia[idx]) {
          base.por_dia[idx].receita_projetada += day.receita_projetada || 0;
          base.por_dia[idx].despesas_cmv += day.despesas_cmv || 0;
          base.por_dia[idx].despesas_outras += day.despesas_outras || 0;
          base.por_dia[idx].despesas_total += day.despesas_total || 0;
          base.por_dia[idx].saldo += day.saldo || 0;
          base.por_dia[idx].despesas_detalhes = [
            ...(base.por_dia[idx].despesas_detalhes || []),
            ...(day.despesas_detalhes || [])
          ];
        }
      });
      
      // Sum por_semana
      data.por_semana?.forEach((week, idx) => {
        if (base.por_semana[idx]) {
          base.por_semana[idx].receita_projetada += week.receita_projetada || 0;
          base.por_semana[idx].despesas_cmv += week.despesas_cmv || 0;
          base.por_semana[idx].despesas_outras += week.despesas_outras || 0;
          base.por_semana[idx].despesas_total += week.despesas_total || 0;
          base.por_semana[idx].saldo += week.saldo || 0;
        }
      });
      
      // Sum por_mes
      data.por_mes?.forEach((month, idx) => {
        if (base.por_mes[idx]) {
          base.por_mes[idx].receita_projetada += month.receita_projetada || 0;
          base.por_mes[idx].despesas_cmv += month.despesas_cmv || 0;
          base.por_mes[idx].despesas_outras += month.despesas_outras || 0;
          base.por_mes[idx].despesas_total += month.despesas_total || 0;
          base.por_mes[idx].saldo += month.saldo || 0;
        }
      });
    }
    
    // Recalculate coverage percentage
    if (base.totais && base.totais.despesas_total > 0) {
      base.totais.cobertura_percentual = (base.totais.receita_projetada / base.totais.despesas_total) * 100;
      base.totais.status = base.totais.saldo >= 0 ? 'superavit' : 'deficit';
    }
    
    return base;
  };

  // State for grouping mode
  const [fluxoGroupBy, setFluxoGroupBy] = useState('dia'); // dia, semana, mes

  // Load data on mount and when year or store changes
  useEffect(() => {
    if (selectedYear && selectedStore) {
      // For "all" option, we need stores to be loaded first
      if (selectedStore === 'all' && availableStores.length === 0) {
        return; // Wait for stores to load
      }
      loadDRE(selectedYear, selectedStore);
      loadProjecao(selectedStore);
      loadFluxoCaixa(selectedStore);
    }
  }, [selectedYear, selectedStore]); // Removed availableStores from dependency to avoid unnecessary re-renders

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  return (
    <div className="space-y-6" data-testid="pnl-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">P&L Gerencial</h1>
          <p className="text-slate-500">Demonstrativo de Resultado do Exercício</p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          {/* Store Selector */}
          <Select value={selectedStore} onValueChange={setSelectedStore} data-testid="store-selector">
            <SelectTrigger className="w-[200px]">
              <Store className="w-4 h-4 mr-2 text-slate-500" />
              <SelectValue placeholder="Selecione a loja" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <span className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Todas as Lojas
                </span>
              </SelectItem>
              {availableStores.map(store => (
                <SelectItem key={store.id} value={store.id}>
                  {STORE_NAMES[store.id] || store.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Year Selector */}
          <Select value={selectedYear?.toString() || ""} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Ano" />
            </SelectTrigger>
            <SelectContent>
              {availableYears.filter(y => y > 2000).map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button onClick={() => { loadDRE(selectedYear, selectedStore); loadProjecao(selectedStore); loadFluxoCaixa(selectedStore); }} variant="outline" size="sm" disabled={!selectedYear}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Store Badge - Shows selected store */}
      {selectedStore && (
        <div className="flex items-center gap-2">
          <Badge variant={selectedStore === 'all' ? 'default' : 'secondary'} className="text-sm">
            <Store className="w-3 h-3 mr-1" />
            {STORE_NAMES[selectedStore] || selectedStore}
          </Badge>
          {selectedStore === 'all' && (
            <span className="text-xs text-slate-500">
              (consolidado de {availableStores.length} lojas)
            </span>
          )}
        </div>
      )}

      {/* Summary Cards */}
      {dreData?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-emerald-600">Receita Bruta</span>
              </div>
              <p className="text-xl font-bold text-emerald-700">
                {formatCurrency(dreData.summary.receita_bruta)}
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-600">CMV</span>
              </div>
              <p className="text-xl font-bold text-red-700">
                {formatCurrency(dreData.summary.cmv)}
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-600">Lucro Bruto</span>
              </div>
              <p className="text-xl font-bold text-blue-700">
                {formatCurrency(dreData.summary.lucro_bruto)}
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-600">Despesas</span>
              </div>
              <p className="text-xl font-bold text-amber-700">
                {formatCurrency(dreData.summary.despesas_totais)}
              </p>
            </CardContent>
          </Card>
          
          <Card className={`bg-gradient-to-br ${dreData.summary.resultado_liquido >= 0 ? 'from-emerald-50 to-emerald-100 border-emerald-200' : 'from-red-50 to-red-100 border-red-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                {dreData.summary.resultado_liquido >= 0 ? 
                  <TrendingUp className="w-4 h-4 text-emerald-600" /> :
                  <TrendingDown className="w-4 h-4 text-red-600" />
                }
                <span className={`text-sm ${dreData.summary.resultado_liquido >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  Resultado
                </span>
              </div>
              <p className={`text-xl font-bold ${dreData.summary.resultado_liquido >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                {formatCurrency(dreData.summary.resultado_liquido)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-xl grid-cols-3 bg-slate-100">
          <TabsTrigger value="dre" className="data-[state=active]:bg-white">
            <DollarSign className="w-4 h-4 mr-2" />
            DRE
          </TabsTrigger>
          <TabsTrigger value="fluxo" className="data-[state=active]:bg-white">
            <TrendingUp className="w-4 h-4 mr-2" />
            Fluxo de Caixa
          </TabsTrigger>
          <TabsTrigger value="projecao" className="data-[state=active]:bg-white">
            <Calendar className="w-4 h-4 mr-2" />
            Despesas 30 dias
          </TabsTrigger>
        </TabsList>

        {/* DRE Tab */}
        <TabsContent value="dre" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Demonstrativo de Resultado - {selectedYear}</CardTitle>
              <CardDescription>Clique nas categorias para expandir</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : dreData ? (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-slate-100 sticky top-0 z-10">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700 sticky left-0 bg-slate-100 min-w-[200px]">
                          Categoria
                        </th>
                        {dreData.months?.map(m => (
                          <th key={m.number} className="text-right py-3 px-3 font-semibold text-slate-700 min-w-[100px]">
                            {m.name}
                          </th>
                        ))}
                        <th className="text-right py-3 px-4 font-semibold text-slate-700 bg-slate-200 sticky right-0 min-w-[120px]">
                          Total {selectedYear}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dreData.categories?.map(category => (
                        <DRECategoryRow
                          key={category.id}
                          category={category}
                          months={dreData.months || []}
                          expanded={expandedCategories[category.id]}
                          onToggle={toggleCategory}
                        />
                      ))}
                      
                      {/* Resultado Líquido Row */}
                      <tr className="bg-slate-800 text-white">
                        <td className="py-4 px-4 sticky left-0 bg-slate-800">
                          <span className="font-bold text-lg">= Resultado Líquido</span>
                        </td>
                        {dreData.months?.map(m => {
                          const revenue = dreData.categories?.find(c => c.id === 'receita_bruta')?.totals[m.number] || 0;
                          const expenses = dreData.categories?.filter(c => c.type === 'expense' || c.type === 'cost')
                            .reduce((sum, c) => sum + (c.totals[m.number] || 0), 0);
                          const result = revenue - expenses;
                          return (
                            <td key={m.number} className="py-4 px-3 text-right">
                              <span className={`font-bold ${result >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {formatCurrency(result)}
                              </span>
                            </td>
                          );
                        })}
                        <td className="py-4 px-4 text-right bg-slate-900 sticky right-0">
                          <span className={`font-bold text-lg ${dreData.summary?.resultado_liquido >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatCurrency(dreData.summary?.resultado_liquido)}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  Nenhum dado disponível
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fluxo de Caixa Tab */}
        <TabsContent value="fluxo" className="mt-4">
          <div className="space-y-6">
            {loading && !fluxoCaixaData ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : fluxoCaixaData ? (
              <>
                {/* Status Card - Main indicator */}
                <Card className={`border-2 ${
                  fluxoCaixaData.totais?.status === 'superavit' 
                    ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-400' 
                    : 'bg-gradient-to-br from-red-50 to-red-100 border-red-400'
                }`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-sm font-medium ${
                          fluxoCaixaData.totais?.status === 'superavit' ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          Projeção de Fluxo de Caixa - Próximos {fluxoCaixaDays} dias
                        </p>
                        <p className={`text-3xl font-bold mt-1 ${
                          fluxoCaixaData.totais?.status === 'superavit' ? 'text-emerald-700' : 'text-red-700'
                        }`}>
                          {formatCurrency(fluxoCaixaData.totais?.saldo)}
                        </p>
                        <div className="flex gap-6 mt-2 text-sm">
                          <span className="text-emerald-600">
                            Receita: {formatCurrency(fluxoCaixaData.totais?.receita_projetada)}
                          </span>
                          <span className="text-red-600">
                            Despesas: {formatCurrency(fluxoCaixaData.totais?.despesas_total)}
                          </span>
                        </div>
                      </div>
                      <div className={`p-4 rounded-full ${
                        fluxoCaixaData.totais?.status === 'superavit' ? 'bg-emerald-200' : 'bg-red-200'
                      }`}>
                        {fluxoCaixaData.totais?.status === 'superavit' ? (
                          <TrendingUp className="w-10 h-10 text-emerald-600" />
                        ) : (
                          <TrendingDown className="w-10 h-10 text-red-600" />
                        )}
                      </div>
                    </div>
                    
                    {/* Coverage Bar */}
                    <div className="mt-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">Cobertura</span>
                        <span className={`font-medium ${
                          fluxoCaixaData.totais?.cobertura_percentual >= 100 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {fluxoCaixaData.totais?.cobertura_percentual?.toFixed(1)}%
                        </span>
                      </div>
                      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${
                            fluxoCaixaData.totais?.cobertura_percentual >= 100 ? 'bg-emerald-500' : 
                            fluxoCaixaData.totais?.cobertura_percentual >= 80 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(fluxoCaixaData.totais?.cobertura_percentual || 0, 100)}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Period Selection Buttons */}
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-sm font-medium text-slate-600">Período:</span>
                      
                      {/* Quick select buttons */}
                      <div className="flex flex-wrap gap-2">
                        {[10, 15, 20, 30, 40, 50, 60].map(days => (
                          <button
                            key={days}
                            onClick={() => {
                              setFluxoCaixaDays(days);
                              setShowCustomDays(false);
                              loadFluxoCaixa(selectedStore, days);
                            }}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                              fluxoCaixaDays === days && !showCustomDays
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {days} dias
                          </button>
                        ))}
                      </div>
                      
                      {/* Custom days button and input */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowCustomDays(!showCustomDays)}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all flex items-center gap-1 ${
                            showCustomDays
                              ? 'bg-purple-600 text-white shadow-sm'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          <Settings className="w-4 h-4" />
                          Personalizar
                        </button>
                        
                        {showCustomDays && (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              max="365"
                              value={customDaysInput}
                              onChange={(e) => setCustomDaysInput(e.target.value)}
                              placeholder="Dias"
                              className="w-20 px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <button
                              onClick={() => {
                                const days = parseInt(customDaysInput);
                                if (days > 0 && days <= 365) {
                                  setFluxoCaixaDays(days);
                                  loadFluxoCaixa(selectedStore, days);
                                }
                              }}
                              disabled={!customDaysInput || parseInt(customDaysInput) <= 0}
                              className="px-3 py-1.5 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Aplicar
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Grouping Toggle */}
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-700">Detalhamento</h3>
                  <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
                    <button
                      onClick={() => setFluxoGroupBy('dia')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        fluxoGroupBy === 'dia' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Por Dia
                    </button>
                    <button
                      onClick={() => setFluxoGroupBy('semana')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        fluxoGroupBy === 'semana' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Por Semana
                    </button>
                    <button
                      onClick={() => setFluxoGroupBy('mes')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        fluxoGroupBy === 'mes' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Por Mês
                    </button>
                  </div>
                </div>

                {/* Daily Table */}
                {fluxoGroupBy === 'dia' && (
                  <Card>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[800px]">
                          <thead className="bg-slate-100 sticky top-0">
                            <tr>
                              <th className="text-left py-3 px-4 font-semibold text-slate-700 sticky left-0 bg-slate-100 z-10 min-w-[160px]">
                                Linha
                              </th>
                              {fluxoCaixaData.por_dia?.map((day, idx) => (
                                <th key={idx} className={`text-center py-2 px-2 font-medium text-xs min-w-[70px] ${
                                  day.weekday === 'Dom' || day.weekday === 'Sáb' ? 'bg-slate-200' : ''
                                }`}>
                                  <div className="text-slate-500">{day.weekday}</div>
                                  <div className="text-slate-700">{day.date_display}</div>
                                </th>
                              ))}
                              <th className="text-right py-3 px-4 font-semibold text-slate-700 bg-slate-200 sticky right-0 min-w-[100px]">
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Receita Row - PROJETADA */}
                            <tr className="bg-emerald-50 border-b border-emerald-100">
                              <td className="py-2 px-4 font-semibold text-emerald-700 sticky left-0 bg-emerald-50">
                                <div className="flex items-center gap-2">
                                  <TrendingUp className="w-4 h-4" />
                                  Receita Projetada
                                </div>
                                <span className="text-xs font-normal text-emerald-500">(baseado na média)</span>
                              </td>
                              {fluxoCaixaData.por_dia?.map((day, idx) => (
                                <td key={idx} className={`py-2 px-2 text-center text-sm ${
                                  day.weekday === 'Dom' || day.weekday === 'Sáb' ? 'bg-emerald-100/50' : ''
                                }`}>
                                  <span className="text-emerald-600 font-medium">
                                    {(day.receita_projetada / 1000).toFixed(1)}k
                                  </span>
                                </td>
                              ))}
                              <td className="py-2 px-4 text-right font-bold text-emerald-700 bg-emerald-100 sticky right-0">
                                {formatCurrency(fluxoCaixaData.totais?.receita_projetada)}
                              </td>
                            </tr>
                            
                            {/* CMV Row - REAL do banco */}
                            <tr className="bg-blue-50 border-b border-blue-100">
                              <td className="py-2 px-4 font-semibold text-blue-700 sticky left-0 bg-blue-50">
                                <div className="flex items-center gap-2">
                                  <Package className="w-4 h-4" />
                                  (-) CMV
                                </div>
                                <span className="text-xs font-normal text-blue-500">(contas a pagar)</span>
                              </td>
                              {fluxoCaixaData.por_dia?.map((day, idx) => (
                                <td key={idx} className={`py-2 px-2 text-center text-sm ${
                                  day.weekday === 'Dom' || day.weekday === 'Sáb' ? 'bg-blue-100/50' : ''
                                }`}>
                                  {day.despesas_cmv > 0 ? (
                                    <button
                                      onClick={() => setExpandedDay(
                                        expandedDay?.date === day.date && expandedDay?.type === 'cmv' 
                                          ? null 
                                          : { date: day.date, type: 'cmv' }
                                      )}
                                      className={`text-blue-600 font-medium hover:underline cursor-pointer ${
                                        expandedDay?.date === day.date && expandedDay?.type === 'cmv' ? 'underline' : ''
                                      }`}
                                    >
                                      {formatCurrency(day.despesas_cmv)}
                                    </button>
                                  ) : (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </td>
                              ))}
                              <td className="py-2 px-4 text-right font-bold text-blue-700 bg-blue-100 sticky right-0">
                                {formatCurrency(fluxoCaixaData.totais?.despesas_cmv)}
                              </td>
                            </tr>
                            
                            {/* Outras Despesas Row - REAL do banco */}
                            <tr className="bg-amber-50 border-b border-amber-100">
                              <td className="py-2 px-4 font-semibold text-amber-700 sticky left-0 bg-amber-50">
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4" />
                                  (-) Outras Despesas
                                </div>
                                <span className="text-xs font-normal text-amber-500">(contas a pagar)</span>
                              </td>
                              {fluxoCaixaData.por_dia?.map((day, idx) => (
                                <td key={idx} className={`py-2 px-2 text-center text-sm ${
                                  day.weekday === 'Dom' || day.weekday === 'Sáb' ? 'bg-amber-100/50' : ''
                                }`}>
                                  {day.despesas_outras > 0 ? (
                                    <button
                                      onClick={() => setExpandedDay(
                                        expandedDay?.date === day.date && expandedDay?.type === 'outras' 
                                          ? null 
                                          : { date: day.date, type: 'outras' }
                                      )}
                                      className={`text-amber-600 font-medium hover:underline cursor-pointer ${
                                        expandedDay?.date === day.date && expandedDay?.type === 'outras' ? 'underline' : ''
                                      }`}
                                    >
                                      {formatCurrency(day.despesas_outras)}
                                    </button>
                                  ) : (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </td>
                              ))}
                              <td className="py-2 px-4 text-right font-bold text-amber-700 bg-amber-100 sticky right-0">
                                {formatCurrency(fluxoCaixaData.totais?.despesas_outras)}
                              </td>
                            </tr>
                            
                            {/* Saldo Row */}
                            <tr className="bg-slate-800 text-white">
                              <td className="py-3 px-4 font-bold sticky left-0 bg-slate-800">
                                = Saldo do Dia
                              </td>
                              {fluxoCaixaData.por_dia?.map((day, idx) => (
                                <td key={idx} className="py-3 px-2 text-center">
                                  <span className={`font-bold text-sm ${
                                    day.saldo >= 0 ? 'text-emerald-400' : 'text-red-400'
                                  }`}>
                                    {day.despesas_total > 0 || day.receita_projetada > 0 
                                      ? formatCurrency(day.saldo)
                                      : '-'}
                                  </span>
                                </td>
                              ))}
                              <td className={`py-3 px-4 text-right font-bold text-lg sticky right-0 ${
                                fluxoCaixaData.totais?.saldo >= 0 ? 'bg-emerald-700' : 'bg-red-700'
                              }`}>
                                {formatCurrency(fluxoCaixaData.totais?.saldo)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Detail Panel - shows when a day is expanded */}
                      {expandedDay && (
                        <div className="border-t-2 border-slate-300 p-4 bg-slate-50">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-slate-700">
                              {expandedDay.type === 'cmv' ? '📦 Detalhes CMV' : '🏢 Detalhes Outras Despesas'} - {
                                fluxoCaixaData.por_dia?.find(d => d.date === expandedDay.date)?.date_display
                              }
                            </h4>
                            <button 
                              onClick={() => setExpandedDay(null)}
                              className="text-slate-400 hover:text-slate-600"
                            >
                              ✕
                            </button>
                          </div>
                          <div className="space-y-2">
                            {fluxoCaixaData.por_dia?.find(d => d.date === expandedDay.date)?.despesas_detalhes
                              ?.filter(det => expandedDay.type === 'cmv' ? det.is_cmv : !det.is_cmv)
                              .map((det, idx) => (
                                <div key={idx} className={`p-3 rounded-lg border ${
                                  expandedDay.type === 'cmv' 
                                    ? 'bg-blue-50 border-blue-200' 
                                    : 'bg-amber-50 border-amber-200'
                                }`}>
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-medium text-slate-800">{det.fornecedor || 'Fornecedor não informado'}</p>
                                      <p className="text-sm text-slate-500">{det.categoria || 'Sem categoria'}</p>
                                    </div>
                                    <p className={`font-bold ${
                                      expandedDay.type === 'cmv' ? 'text-blue-700' : 'text-amber-700'
                                    }`}>
                                      {formatCurrency(det.valor)}
                                    </p>
                                  </div>
                                </div>
                              ))
                            }
                            {fluxoCaixaData.por_dia?.find(d => d.date === expandedDay.date)?.despesas_detalhes
                              ?.filter(det => expandedDay.type === 'cmv' ? det.is_cmv : !det.is_cmv).length === 0 && (
                              <p className="text-slate-500 text-center py-4">Nenhuma despesa nesta categoria para este dia</p>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Weekly View */}
                {fluxoGroupBy === 'semana' && (
                  <Card>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left py-3 px-4 font-semibold text-slate-700">Linha</th>
                              {fluxoCaixaData.por_semana?.map((week, idx) => (
                                <th key={idx} className="text-center py-3 px-4 font-semibold text-slate-700 min-w-[140px]">
                                  {week.label || `Semana ${week.semana}`}
                                </th>
                              ))}
                              <th className="text-right py-3 px-4 font-semibold text-slate-700 bg-slate-200">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="bg-emerald-50 border-b">
                              <td className="py-3 px-4 font-semibold text-emerald-700">Receita Projetada</td>
                              {fluxoCaixaData.por_semana?.map((week, idx) => (
                                <td key={idx} className="py-3 px-4 text-center font-medium text-emerald-600">
                                  {formatCurrency(week.receita_projetada)}
                                </td>
                              ))}
                              <td className="py-3 px-4 text-right font-bold text-emerald-700 bg-emerald-100">
                                {formatCurrency(fluxoCaixaData.totais?.receita_projetada)}
                              </td>
                            </tr>
                            <tr className="bg-blue-50 border-b">
                              <td className="py-3 px-4 font-semibold text-blue-700">(-) CMV</td>
                              {fluxoCaixaData.por_semana?.map((week, idx) => (
                                <td key={idx} className="py-3 px-4 text-center font-medium text-blue-600">
                                  {formatCurrency(week.despesas_cmv)}
                                </td>
                              ))}
                              <td className="py-3 px-4 text-right font-bold text-blue-700 bg-blue-100">
                                {formatCurrency(fluxoCaixaData.totais?.despesas_cmv)}
                              </td>
                            </tr>
                            <tr className="bg-amber-50 border-b">
                              <td className="py-3 px-4 font-semibold text-amber-700">(-) Outras Despesas</td>
                              {fluxoCaixaData.por_semana?.map((week, idx) => (
                                <td key={idx} className="py-3 px-4 text-center font-medium text-amber-600">
                                  {formatCurrency(week.despesas_outras)}
                                </td>
                              ))}
                              <td className="py-3 px-4 text-right font-bold text-amber-700 bg-amber-100">
                                {formatCurrency(fluxoCaixaData.totais?.despesas_outras)}
                              </td>
                            </tr>
                            <tr className="bg-slate-800 text-white">
                              <td className="py-3 px-4 font-bold">= Saldo</td>
                              {fluxoCaixaData.por_semana?.map((week, idx) => (
                                <td key={idx} className="py-3 px-4 text-center">
                                  <span className={`font-bold ${week.saldo >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {formatCurrency(week.saldo)}
                                  </span>
                                </td>
                              ))}
                              <td className={`py-3 px-4 text-right font-bold ${
                                fluxoCaixaData.totais?.saldo >= 0 ? 'bg-emerald-700' : 'bg-red-700'
                              }`}>
                                {formatCurrency(fluxoCaixaData.totais?.saldo)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Monthly View */}
                {fluxoGroupBy === 'mes' && (
                  <Card>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left py-3 px-4 font-semibold text-slate-700">Linha</th>
                              {fluxoCaixaData.por_mes?.map((month, idx) => (
                                <th key={idx} className="text-center py-3 px-4 font-semibold text-slate-700 min-w-[120px]">
                                  {month.mes_nome}
                                  <span className="text-xs text-slate-400 block">({month.dias_count} dias)</span>
                                </th>
                              ))}
                              <th className="text-right py-3 px-4 font-semibold text-slate-700 bg-slate-200">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="bg-emerald-50 border-b">
                              <td className="py-3 px-4 font-semibold text-emerald-700">Receita Projetada</td>
                              {fluxoCaixaData.por_mes?.map((month, idx) => (
                                <td key={idx} className="py-3 px-4 text-center font-medium text-emerald-600">
                                  {formatCurrency(month.receita_projetada)}
                                </td>
                              ))}
                              <td className="py-3 px-4 text-right font-bold text-emerald-700 bg-emerald-100">
                                {formatCurrency(fluxoCaixaData.totais?.receita_projetada)}
                              </td>
                            </tr>
                            <tr className="bg-blue-50 border-b">
                              <td className="py-3 px-4 font-semibold text-blue-700">(-) CMV</td>
                              {fluxoCaixaData.por_mes?.map((month, idx) => (
                                <td key={idx} className="py-3 px-4 text-center font-medium text-blue-600">
                                  {formatCurrency(month.despesas_cmv)}
                                </td>
                              ))}
                              <td className="py-3 px-4 text-right font-bold text-blue-700 bg-blue-100">
                                {formatCurrency(fluxoCaixaData.totais?.despesas_cmv)}
                              </td>
                            </tr>
                            <tr className="bg-amber-50 border-b">
                              <td className="py-3 px-4 font-semibold text-amber-700">(-) Outras Despesas</td>
                              {fluxoCaixaData.por_mes?.map((month, idx) => (
                                <td key={idx} className="py-3 px-4 text-center font-medium text-amber-600">
                                  {formatCurrency(month.despesas_outras)}
                                </td>
                              ))}
                              <td className="py-3 px-4 text-right font-bold text-amber-700 bg-amber-100">
                                {formatCurrency(fluxoCaixaData.totais?.despesas_outras)}
                              </td>
                            </tr>
                            <tr className="bg-slate-800 text-white">
                              <td className="py-3 px-4 font-bold">= Saldo</td>
                              {fluxoCaixaData.por_mes?.map((month, idx) => (
                                <td key={idx} className="py-3 px-4 text-center">
                                  <span className={`font-bold ${month.saldo >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {formatCurrency(month.saldo)}
                                  </span>
                                </td>
                              ))}
                              <td className={`py-3 px-4 text-right font-bold ${
                                fluxoCaixaData.totais?.saldo >= 0 ? 'bg-emerald-700' : 'bg-red-700'
                              }`}>
                                {formatCurrency(fluxoCaixaData.totais?.saldo)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Carregando dados de fluxo de caixa...</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Projeção Tab */}
        <TabsContent value="projecao" className="mt-4">
          <div className="space-y-6">
            {/* Resumo da Projeção */}
            {projecaoData && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-600 font-medium">Custo de Mercadoria</p>
                          <p className="text-2xl font-bold text-blue-700">
                            {formatCurrency(projecaoData.resumo?.cmv?.total)}
                          </p>
                          <p className="text-xs text-blue-500">{projecaoData.resumo?.cmv?.count} parcelas</p>
                        </div>
                        <Package className="w-10 h-10 text-blue-300" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-amber-600 font-medium">Outras Despesas</p>
                          <p className="text-2xl font-bold text-amber-700">
                            {formatCurrency(projecaoData.resumo?.outras_despesas?.total)}
                          </p>
                          <p className="text-xs text-amber-500">{projecaoData.resumo?.outras_despesas?.count} parcelas</p>
                        </div>
                        <Building2 className="w-10 h-10 text-amber-300" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-red-600 font-medium">Total a Pagar</p>
                          <p className="text-2xl font-bold text-red-700">
                            {formatCurrency(projecaoData.resumo?.total_geral)}
                          </p>
                          <p className="text-xs text-red-500">Próximos 30 dias</p>
                        </div>
                        <AlertCircle className="w-10 h-10 text-red-300" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Por Semana */}
                {projecaoData.por_semana?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Distribuição por Semana</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {projecaoData.por_semana.map((week, idx) => (
                          <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-slate-700">{week.label}</span>
                              <span className="font-bold text-slate-800">{formatCurrency(week.total)}</span>
                            </div>
                            <div className="flex gap-4">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-blue-500" />
                                <span className="text-sm text-slate-600">CMV: {formatCurrency(week.cmv)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-amber-500" />
                                <span className="text-sm text-slate-600">Outras: {formatCurrency(week.outras)}</span>
                              </div>
                            </div>
                            {/* Progress bar */}
                            <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden flex">
                              <div 
                                className="bg-blue-500 h-full" 
                                style={{ width: `${week.total > 0 ? (week.cmv / week.total) * 100 : 0}%` }} 
                              />
                              <div 
                                className="bg-amber-500 h-full" 
                                style={{ width: `${week.total > 0 ? (week.outras / week.total) * 100 : 0}%` }} 
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Outras Despesas por Categoria */}
                {projecaoData.outras_por_categoria && Object.keys(projecaoData.outras_por_categoria).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Outras Despesas por Categoria</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(projecaoData.outras_por_categoria).slice(0, 10).map(([cat, data]) => (
                          <div key={cat} className="flex items-center justify-between p-2 rounded hover:bg-slate-50">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {data.items?.length || 0}
                              </Badge>
                              <span className="text-sm text-slate-700">{cat}</span>
                            </div>
                            <span className="font-medium text-slate-800">{formatCurrency(data.total)}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PnLPage;
